import type { SchedulerType } from '@/features/nylas-calendar-manage-connection-modal';
import type { AxiosPromise, AxiosResponse, CancelToken } from 'axios';
import type { FullATSVacancyResponse, VacancyWithAtsJob } from 'clients/AutoCompleteClient/types';
import type { OutreachTemplateType } from 'clients/OutreachTemplateclient/OutreachTemplateClient.types';
import type { JobImprovedGetDetailOutput } from 'clients/ReachoutClient/ReachoutClient.types';
import type { Candidate } from 'clients/SearchClient/SearchClient.types';
import type {
  BaseCallback,
  BasePagination,
  EmptyResponseCallback,
  PaginatedFilters,
  PaginatedResponse,
} from 'clients/types';
import type { CandidateHistoryAction, CandidateStatus } from 'services/candidates/types';
import type { Filters } from 'shared/legacy/contexts/SearchContext/FiltersContext/types';
import type { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { Ats } from 'types/ats';
import type { ICampaign } from 'types/campaign';
import type { CampaignOverviewStatus } from './CampaignOverviewStatus.types';
import type { CandidateResponse } from './CandidateResponse';

type CampaignUser = {
  full_name: string;
  initials: string;
};

export type RatedCandidates = {
  id: number;
  es_person_id: string;
};

export type MarkActivityLogAsReadResponse = {
  id: number;
  is_new_message: boolean;
};

export type CheckUnreadButtonAvailabilityResponse = {
  is_available: boolean;
};

export type HasMessagesResponse = {
  has_messages: boolean;
};

export enum MatchingRateQuality {
  Excellent = 'excellent',
  VeryGood = 'very_good',
  Good = 'good',
  Fair = 'fair',
  Moderate = 'moderate',
  Poor = 'poor',
}

export type MatchingRate = {
  percentage: number;
  quality: MatchingRateQuality;
  raw_score?: number;
};

export interface CampaignResponse {
  id: number;
  connected_ats: VacancyWithAtsJob | null;
  is_ats_configured?: boolean;
  tenant_id: number;
  tenant_domain: string;
  user: CampaignUser;
  name: string;
  rated_candidates: RatedCandidates[];
  legacy_assigment_id?: number;
  created_at: Date;
  updated_at: Date;
  status: string;
  last_viewed?: Date;
  last_synced?: Date;
  total_candidates_count: number;
  new_candidates_count?: number;
  approved_candidates_count?: number;
  rejected_candidates_count?: number;
  rated_candidates_count?: number;
  result_callback_url?: string;
  current_filter: {
    filters: Filters;
  };
}

export interface CampaignFacetsResponse {
  took: number;
  timed_out: boolean;
  _shards: {
    total: number;
    successful: number;
    skipped: number;
    failed: number;
  };
  hits: {
    total: {
      value: number;
      relation: string;
    };
    max_score: null;
    hits: never[];
  };
  aggregations: {
    source_facets: {
      doc_count: number;
      source_facet: {
        doc_count_error_upper_bound: number;
        sum_other_doc_count: number;
        buckets: Array<{
          key: CampaignJobboard;
          doc_count: number;
        }>;
      };
    };
  };
}

export type CampaignOverviewResponse = {
  id: number;
  name: string;
  new_candidates_count: number;
  new_replies_count: number;
  full_owner_name: string;
};

export interface CountResponse {
  count: number;
}

export interface CampaignPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: CampaignResponse[];
}

export type GetCountCampaignsHandler = (params: GetCountCampaignsParams) => AxiosPromise<CountResponse>;
export type GetCountCampaignsCandidatesHandler = () => AxiosPromise<CountResponse>;

export interface GetCountCampaignsParams {
  status: string;
  user_id?: number;
}
export interface CampaignCandidateResponse {
  id: number;
  es_person_id: string;
  legacy_person_id?: string;
  status: CandidateStatus;
  is_unlocked?: boolean | null;
  campaign?: Omit<CampaignResponse, 'created_at' | 'current_filter' | 'rated_candidates' | 'updated_at'>;
  candidate: CandidateResponse;
  created_at: Date;
  updated_at: Date;

  reasons?: string[] | null;
  matching_rate?: MatchingRate;
}

export interface CampaignCreateBody {
  name: string;
  vacancyUrl: string;
  vacancyDocumentIdentifier: string;
  atsVacancy: {
    name: string;
    value: string;
  };
  isATSConfigured: boolean;
  filters?: Filters;
}

export interface CampaignFiltersBody {
  filters: Filters;
}

export type NotifyCandidateStatus = 'success';

export type NotifyCandidateMethod = OutreachTemplateType | 'call';

export type NotifyCandidateData = {
  subject?: string;
  message?: string;
  sign_off?: string;
  email?: string;
  phone_number?: string;
  website?: string;
  status?: NotifyCandidateStatus;
  provider_thread_id: string | undefined;
  metadata?: NotifyCandidateMetadata;
  is_ai_leveraged?: boolean;
  schedule_follow_up_to?: Date | Date[];
  similarity_score?: number;
  was_ai_message_modified?: boolean;
  job_improved_id?: string | number;
  origin?: string;
};

export type NotifyCandidateMetadata = IndeedMetadata | {};

type IndeedMetadata = {
  job_info: {
    jobTitle: string;
  };
};

type ActivityLogUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

export type CandidateActivityLogResponse = {
  id: number | '';
  campaign: Pick<CampaignResponse, 'id' | 'name'>;
  es_person_id: string;
  action: CandidateHistoryAction;
  tenant_id: number;
  tenant_domain: string;
  user: ActivityLogUser;
  created_at: string;
  source: string;
  is_new_message: boolean;
  metadata: {
    reasons?: string[];
    added_labels?: string[];
    original?: string[];
    removed_labels?: string[];
  };
  date: string;
  candidate_notification?: CandidateNotification;
};

export type CandidateHistoryResponse = Exclude<CandidateActivityLogResponse, 'source'> & {
  content: string;
  subject: string;
  ats?: { ats_name: Ats };
};

export type GetSearchResultsParams = BasePagination & {
  campaignId: number;
  cancelToken?: CancelToken;
  useVectorSearch?: boolean;
};

/////////////////////////////
/// Params
/////////////////////////////

export type NotifyCandidateParams = {
  campaign_id: CampaignResponse['id'];
  candidate_id: string;
  send_method: NotifyCandidateMethod;
  data: NotifyCandidateData;
  attachments?: File[];
};

export type GetCandidateActivityLogParams = {
  candidate_id: string;
};

export type MarkActivityLogAsReadParams = {
  activity_log_id: string | number;
};

export type CheckUnreadButtonAvailability = {
  es_person_id: string;
};

export type MarkActivityLogAsReadBasedOnEsPersonIdParams = {
  es_person_id: string;
};

export type MarkAsUnreadParams = {
  es_person_id: string;
  campaign_id: number | string;
};

export type GetCandidateHistoryParams = {
  candidate_id: string;
};

export type GetCampaignsParams = {
  search?: string;
  legacy_assigment_id?: number;
};

export type ChangeOwnerParams = {
  campaignId: number | string;
  userId: number | string;
};

export type GetCampaignCandidateCVParams = {
  campaignId: string;
  candidateId: string;
  options: any;
};

export type GetCampaignCandidateCVURLParams = {
  cvFileId: string;
  shouldUseRedact?: boolean;
};

export type GetCampaignCandidateParams = {
  campaignId: string | number;
  esPersonId: string;
};

export type GetCampaignCandidatesTabParams = PaginatedFilters<{
  id: number;
  status: CandidateStatus;
  labels?: string[];
  cancelToken?: CancelToken;
}>;

export type CreateTextSearchParams = {
  job_description: string;
};

export type CreateAtsSearchParams = {
  ats_id: string;
};

export type GenerateReachoutMessageParams = {
  vacancy: { name: string; description: string } & Partial<FullATSVacancyResponse>;
  candidate: { es_person_id: string; resume: string };
  outreach_type: Extract<CandidateOutreachSendMethod, 'email' | 'linkedin_inmail' | 'indeed' | 'whatsapp'>;
  language: string;
  origin?: string;
};

export type GetVectorSearchStatusParams = void;

export type GetCampaignVectorSearchStatusParams = {
  campaignId: string | number;
};

export type StatusChangeFeedbackParams = { key: string; value: string }[];
/////////////////////////////
/// Responses
/////////////////////////////

export type NotifyCandidateResponse = {
  status: NotifyCandidateStatus;
  candidate_notification_id: number;
};

export type CampaignCandidateSaveNotesResponse = {
  id: number;
  content: string;
  created_at: Date;
  updated_at: Date;
  es_person_id: string;
  campaign: number;
  created_by: {
    full_name: string;
    initials: string;
  };
};

export type CampaignCandidateLabelsResponse = {
  id: number;
  reasons: string[];
};

export type GetCampaignNewRepliesResponse = {
  new_replies: number;
};

export type CreateCustomLogParams = {
  es_person_id: string;
  source: string;
  campaign: string | number;
  contacted_channel?: CandidateOutreachSendMethod;
  action: CandidateHistoryAction;
  metadata?: Record<string, unknown>;
};

export type CreateCustomLogResponse = {
  success: boolean;
};

export type GenerateReachoutMessageResponse = {
  subject: string;
  message: string;
};

export interface VectorSearchStatusResponse {
  vector_search_enabled: boolean;
  features: {
    [key: string]: boolean;
  };
}

export interface CampaignVectorSearchStatusResponse {
  vector_search_enabled: boolean;
}

/////////////////////////////
/// Handlers
/////////////////////////////

export type GetCampaignsHandler = (params: GetCampaignsParams) => BaseCallback<CampaignResponse[]>;

export type ChangeOwnerHandler = (params: ChangeOwnerParams) => BaseCallback<CampaignResponse>;

export type MarkActivityLogAsReadHandler = (
  params: MarkActivityLogAsReadParams,
) => BaseCallback<MarkActivityLogAsReadResponse>;

export type CheckUnreadButtonAvailabilityHandler = (
  params: CheckUnreadButtonAvailability,
) => BaseCallback<CheckUnreadButtonAvailabilityResponse>;

export type MarkActivityLogAsReadBasedOnEsPersonIdHandler = (
  params: MarkActivityLogAsReadBasedOnEsPersonIdParams,
) => BaseCallback<void>;

export type MarkAsUnreadHandler = (params: MarkAsUnreadParams) => BaseCallback<any>;
export type BatchReadHandler = () => BaseCallback<any>;

export type GetCampaignHandler = (campaignId: number | string) => BaseCallback<CampaignResponse>;

export type GetInitialCampaignHandler = () => BaseCallback<CampaignResponse>;

export type CreateCampaignHandler = (data: Partial<CampaignResponse>) => BaseCallback<CampaignResponse>;

export type UpdateCampaignHandler = (
  campaignId: number,
  data: Partial<CampaignResponse>,
) => BaseCallback<CampaignResponse>;

export type GetSearchResultsHandler = (params: GetSearchResultsParams) => BaseCallback<any>;

export type GetCampaignsPageHandler = (
  params: PaginatedFilters<CampaignResponse>,
) => BaseCallback<PaginatedResponse<CampaignResponse>>;

export type NotifyCandidateHandler = (params: NotifyCandidateParams) => BaseCallback<NotifyCandidateResponse>;

export type GetFacetsHandler = (id: number | string) => BaseCallback<CampaignFacetsResponse>;

export type GetCampaignsOverviewHandler = (params: {
  status: CampaignOverviewStatus;
  limit: number;
  offset: number;
  name?: string;
  /**
   * If passed, it'll only fetch campaigns owned by this user id
   */
  user_id?: number;
}) => BaseCallback<PaginatedResponse<CampaignOverviewResponse>>;

export type GetCandidateActivityLogHandler = (
  params: PaginatedFilters<GetCandidateActivityLogParams>,
) => BaseCallback<PaginatedResponse<CandidateActivityLogResponse>>;

export type GetCandidateHistoryHandler = (
  params: PaginatedFilters<GetCandidateHistoryParams>,
) => BaseCallback<CandidateHistoryResponse[]>;

export type GetHasMessagesHandler = (params: { es_person_id: string }) => BaseCallback<HasMessagesResponse>;

export type GetCampaignCandidateCVHandler = (params: GetCampaignCandidateCVParams) => BaseCallback<Blob | undefined>;
export type GetCampaignCandidateCVURLHandler = (
  params: GetCampaignCandidateCVURLParams,
) => BaseCallback<{ url: string }>;
export type GetFileHandler = (url: string) => BaseCallback<Blob | undefined>;

export type DownloadCampaignCandidateCVHandler = (
  campaignId: number,
  candidateId: string,
  name?: string,
  fileExtension?: string,
) => Promise<void>;

export type PostApproveCampaignCandidateHandler = (params: { campaignId: number; candidateId: string }) => Promise<any>;

export type GetCampaignRatedCandidatesHandler = (
  params: GetCampaignCandidatesTabParams,
) => BaseCallback<PaginatedResponse<CampaignCandidateResponse>>;

export type GetCampaignCandidateHandler = (
  params: GetCampaignCandidateParams,
) => BaseCallback<CampaignCandidateResponse>;

export type GetCampaignCandidatesStatsHandler = (id: number) => BaseCallback<ICampaign.Stats>;

export type GetCampaignCandidateNotesHandler = (params: {
  es_person_id: string;
}) => BaseCallback<CampaignCandidateSaveNotesResponse[]>;

export type PostCampaignCandidateNotesHandler = (params: {
  es_person_id: string;
  notes: string;
  campaign_id: string | number;
}) => BaseCallback<CampaignCandidateSaveNotesResponse>;

export type PutCampaignCandidateNotesHandler = (params: {
  es_person_id: string;
  note_id: number;
  notes: string;
  campaign_id: string | number;
}) => BaseCallback<CampaignCandidateSaveNotesResponse>;

export type DeleteCampaignCandidateNotesHandler = (params: {
  es_person_id: string;
  note_id: number;
}) => EmptyResponseCallback;

export type PutCampaignCandidateSaveLabelsHandler = (params: {
  es_person_id: string;
  campaign_id: number | string;
  labels: string[];
}) => BaseCallback<CampaignCandidateLabelsResponse>;

export type GetDistinctedLabelsHandler = () => BaseCallback<string[]>;

export type GetCampaignTemplatesHandler = (params: {
  search: string;
  limit?: number;
}) => BaseCallback<PaginatedResponse<CampaignResponse>>;

export type CopyCampaignTemplatesHandler = (params: { templateId: number }) => BaseCallback<CampaignResponse>;

export type CreateTextSearchHandler = ({ job_description }: CreateTextSearchParams) => BaseCallback<CampaignResponse>;

export type CreateAtsSearchHandler = ({ ats_id }: CreateAtsSearchParams) => BaseCallback<CampaignResponse>;

export type GenerateReachoutMessageHandler = (
  params: GenerateReachoutMessageParams,
) => BaseCallback<GenerateReachoutMessageResponse>;

export type GetCampaignNewRepliesHandler = () => AxiosPromise<GetCampaignNewRepliesResponse>;

export type CreateCustomLogHandler = (params: CreateCustomLogParams) => AxiosPromise<CreateCustomLogResponse>;

export type SearchEsCampaignCandidateHandler = ({
  esPersonIds,
}: {
  esPersonIds: string[];
}) => AxiosPromise<SearchEsCampaignCandidateResponse>;

export type SearchEsCampaignCandidateResponse = {
  results: Candidate[];
};

export enum CandidateOutreachSendMethod {
  Linkedin = 'linkedin',
  InMail = 'linkedin_inmail',
  Whatsapp = 'whatsapp',
  Email = 'email',
  Indeed = 'indeed',
  Call = 'call',
  Werknl = 'werknl',
}

export type CandidateNotification = {
  id: number;
  created_at: string;
  send_method: CandidateOutreachSendMethod;
  message: string;
  subject: string;
  sign_off: string;
  status: string;
};

export enum RJCommunicationHubAction {
  Pause = 'pause',
  Resume = 'resume',
}

export type StartRJCommunicationHubPipelineParams = {
  campaign_id: number;
  es_person_id: string;
  subject?: string;
  message?: string;
  improved_job_id?: number;
  schedule_interview?: boolean;
  scheduler_type?: SchedulerType;
  origin?: string;
};

export type ManageRJCommunicationHubPipelineParams = {
  campaign_id: number;
  es_person_id: string;
  action: RJCommunicationHubAction;
};

export type SubmitCandidateInterestParams = {
  token: string;
};

export type StartRJCommunicationHubPipelineHandler = (
  params: StartRJCommunicationHubPipelineParams,
) => AxiosPromise<{ status: string }>;

export type ManageRJCommunicationHubPipelineHandler = (
  params: ManageRJCommunicationHubPipelineParams,
) => AxiosPromise<{ status: string }>;

export type SubmitCandidateInterestHandler = (
  params: SubmitCandidateInterestParams,
) => AxiosPromise<{ message: string }>;

export type CreateRjIndeedOutreachAttemptParams = {
  campaign_id: number;
  es_person_id: string;
  improved_job_id?: number;
  metadata?: {
    indeed_project_key?: string;
    indeed_project_name?: string;
  };
};

export type CreateRjIndeedOutreachAttemptResponse = {
  attempt_id: number;
};

export type CreateRjIndeedOutreachAttemptHandler = (
  params: CreateRjIndeedOutreachAttemptParams,
) => AxiosPromise<CreateRjIndeedOutreachAttemptResponse>;

export type ConfirmRjIndeedOutreachAttemptParams = {
  campaign_id: number;
  es_person_id: string;
  attempt_id: number;
  candidate_notification_id: number;
  questionnaire?: unknown;
  schedule_interview?: boolean;
  scheduler_type?: SchedulerType;
};

export type ConfirmRjIndeedOutreachAttemptResponse = {
  attempt_id: number;
  status: 'confirmed';
};

export type ConfirmRjIndeedOutreachAttemptHandler = (
  params: ConfirmRjIndeedOutreachAttemptParams,
) => AxiosPromise<ConfirmRjIndeedOutreachAttemptResponse>;

export enum RJActivityLogStatus {
  Running = 'running',
  Paused = 'paused',
  Resumed = 'resumed',
  Ended = 'ended',
}

export enum RjIndeedOutreachAttemptStatus {
  PendingSendConfirmation = 'pending_send_confirmation',
  Confirmed = 'confirmed',
  WaitingContactInfo = 'waiting_contact_info',
  Declined = 'declined',
  CommAgentStartInProgress = 'comm_agent_start_in_progress',
  CommAgentStarted = 'comm_agent_started',
  CommAgentStartFailed = 'comm_agent_start_failed',
}

export enum RjIndeedOutreachPhase {
  WaitingForReply = 'waiting_for_reply',
  WaitingForContactInfo = 'waiting_for_contact_info',
}

export type RjIndeedOutreachResponse = {
  attempt_id: number;
  status: RjIndeedOutreachAttemptStatus | string;
  phase?: RjIndeedOutreachPhase | string;
  improved_job_id: number;
  candidate_notification_id: number;
};

export type RJActivityLogResponse = {
  id: number;
  tenant_id: number;
  campaign_id: number;
  user_id: number;
  es_person_id: string;
  job_improved_id: string;
  status: RJActivityLogStatus;
  history: {
    [key: string]: number[];
  };
  indeed_outreach?: RjIndeedOutreachResponse | null;
  created_at: string;
  updated_at: string;
};

export type GetRJActivityLogsParams = {
  es_person_id: string;
  campaign_id: number | string;
};

export type GetRJActivityLogsHandler = (params: GetRJActivityLogsParams) => BaseCallback<RJActivityLogResponse>;

export type RjBulkUsageResponse = {
  sent_count: number;
  daily_rj_bulk_activation_limit: number;
};

export type GetRjBulkUsageHandler = () => BaseCallback<RjBulkUsageResponse>;

export type GetPublicVacancyParams = {
  token: string;
};

export type PublicVacancyFlow = 'email' | 'indeed';

export type PublicVacancyCandidateAction = 'interest_response' | 'questionnaire';

export type PublicVacancyResponse = {
  job: JobImprovedGetDetailOutput & { tenant_name?: string; db_id: number };
  accept_token: string;
  reject_token: string;
  flow?: PublicVacancyFlow;
  candidate_action?: PublicVacancyCandidateAction;
  questionnaire_url?: string | null;
  branding?: {
    logo_url: string | null;
    primary_color: string | null;
  };
};

export type GetPublicVacancyHandler = (params: GetPublicVacancyParams) => BaseCallback<PublicVacancyResponse>;

export type SubmitCandidateFeedbackParams = {
  token: string;
  feedback?: string;
  robin_verified?: boolean;
  communication_opt_out?: boolean;
};

export type SubmitCandidateFeedbackResponse = {
  message: string;
};

export type SubmitCandidateFeedbackHandler = (
  params: SubmitCandidateFeedbackParams,
) => BaseCallback<SubmitCandidateFeedbackResponse>;

export type OptOutCommunicationParams = {
  es_person_id: string;
};

export type OptOutCommunicationResponse = {
  message: string;
};

export type OptOutCommunicationHandler = (
  params: OptOutCommunicationParams,
) => BaseCallback<OptOutCommunicationResponse>;

export type GetVectorSearchStatusHandler = () => Promise<AxiosResponse<VectorSearchStatusResponse>>;

export type GetCampaignVectorSearchStatusHandler = (
  params: GetCampaignVectorSearchStatusParams,
) => Promise<AxiosResponse<CampaignVectorSearchStatusResponse>>;

export type VectorSearchHit = {
  _index: string;
  _id: string;
  _score: number;
  _ignored?: string[];
  _source: any;
  matching_rate?: MatchingRate;
};

export type VectorSearchMatchingCandidatePayload = {
  campaign_id: string;
  k?: number;
  size?: number;
  start?: number;
  min_score?: number;
  use_exclusions?: boolean;
  format_response?: boolean;
  max_total_results?: number;
  min_comprehensive_score?: number;
  min_skills_score?: number;
  additional_filters?: Record<string, string>;
  insight_date_boost?: number;
};

export type VectorSearchMatchingCandidateResponse = {
  took: number;
  timed_out: boolean;
  _shards: unknown;
  hits: {
    total: {
      value: number;
      relation: string;
    };
    max_score: number | null;
    hits: VectorSearchHit[];
  };
  search_metadata: unknown;
};

export type VectorSearchMatchingCandidateHandler = (
  payload: VectorSearchMatchingCandidatePayload,
) => Promise<AxiosResponse<VectorSearchMatchingCandidateResponse>>;

export type TranslateMessagePayload = {
  message: string;
  language: string;
  subject?: string;
};

export type TranslateMessageResponse = {
  translated_message: string;
  original_message: string;
  target_language: string;
  translated_subject?: string;
  original_subject?: string;
};

export type TranslateMessageHandler = (
  payload: TranslateMessagePayload,
) => Promise<AxiosResponse<TranslateMessageResponse>>;
